import type {
  ConversationDto,
  ConversationMediaItemDto,
  ConversationMessageDto,
  ConversationPinnedMessageDto,
  ConversationSendQuote,
  CreateConversationRequest,
} from '@tracedocuments/types';

import { getApiErrorMessage } from './errors';
import { apiFetch, apiUpload, type ChangedSinceResult } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';
import { withNetworkRetry } from './retry';

export async function fetchConversations(
  page = 1,
  pageSize = 50,
  archived = false,
): Promise<ConversationDto[]> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    archived: String(archived),
  });
  const res = await apiFetchWithTimeout(`/api/conversations?${params}`);
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? `conversations list failed (${res.status})`);
  }
  const data = await res.json();
  if (data && typeof data === 'object' && Array.isArray(data.items)) {
    return data.items as ConversationDto[];
  }
  return data as ConversationDto[];
}

/** Incremental sync: only conversations with activity after `sinceIso`, plus a cursor for the next check. */
export async function fetchConversationsChangedSince(
  sinceIso: string,
  archived = false,
): Promise<ChangedSinceResult<ConversationDto>> {
  const params = new URLSearchParams({ since: sinceIso, archived: String(archived) });
  const res = await apiFetchWithTimeout(`/api/conversations?${params}`);

  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? `conversations changed-since failed (${res.status})`);
  }
  return (await res.json()) as ChangedSinceResult<ConversationDto>;
}

export async function createConversation(
  request: CreateConversationRequest,
): Promise<ConversationDto> {
  const res = await apiFetch('/api/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'create conversation failed');
  }
  return res.json();
}

export type FetchConversationMessagesOptions = {
  skip?: number;
  take?: number;
  /** ISO timestamp of the oldest message already loaded (keyset pagination). */
  beforeCreatedAt?: string;
  beforeMessageId?: string;
  timeoutMs?: number;
};

export async function fetchConversationMessages(
  conversationId: string,
  skipOrOptions: number | FetchConversationMessagesOptions = 0,
  take = 50,
  timeoutMs = 15_000,
): Promise<ConversationMessageDto[]> {
  const opts: FetchConversationMessagesOptions =
    typeof skipOrOptions === 'number'
      ? { skip: skipOrOptions, take, timeoutMs }
      : { take: 50, timeoutMs: 15_000, ...skipOrOptions };

  const params = new URLSearchParams();
  params.set('take', String(opts.take ?? 50));
  if (opts.beforeCreatedAt) {
    params.set('before', opts.beforeCreatedAt);
    if (opts.beforeMessageId) params.set('beforeId', opts.beforeMessageId);
  } else {
    params.set('skip', String(opts.skip ?? 0));
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 15_000);
  try {
    const res = await apiFetch(
      `/api/conversations/${conversationId}/messages?${params}`,
      { signal: controller.signal },
    );
    if (!res.ok) {
      const message = await getApiErrorMessage(res);
      throw new Error(message ?? `conversation messages failed (${res.status})`);
    }
    return res.json();
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('conversation messages timeout');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export type ConversationThreadSummary = {
  messages: ConversationMessageDto[];
  pinnedMessages: ConversationPinnedMessageDto[];
};

/** Fase 5 BFF — first messages page + pinned pins in one round-trip. */
export async function fetchConversationThreadSummary(
  conversationId: string,
  skip = 0,
  take = 50,
  timeoutMs = 15_000,
): Promise<ConversationThreadSummary> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await apiFetch(
      `/api/conversations/${conversationId}/thread-summary?skip=${skip}&take=${take}`,
      { signal: controller.signal },
    );
    if (!res.ok) {
      const message = await getApiErrorMessage(res);
      throw new Error(message ?? `conversation thread-summary failed (${res.status})`);
    }
    const raw = (await res.json()) as {
      messages?: ConversationMessageDto[];
      pinnedMessages?: ConversationPinnedMessageDto[];
    };
    return {
      messages: raw.messages ?? [],
      pinnedMessages: raw.pinnedMessages ?? [],
    };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('conversation thread-summary timeout');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function archiveConversation(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/archive`, { method: 'POST' });
  if (!res.ok) throw new Error('archive conversation failed');
}

export async function unarchiveConversation(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/unarchive`, { method: 'POST' });
  if (!res.ok) throw new Error('unarchive conversation failed');
}

export async function muteConversation(conversationId: string, until: Date): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/mute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ until: until.toISOString() }),
  });
  if (!res.ok) throw new Error('mute conversation failed');
}

export async function unmuteConversation(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/unmute`, { method: 'POST' });
  if (!res.ok) throw new Error('unmute conversation failed');
}

export async function setConversationMessageReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}/reaction`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error('set message reaction failed');
  return res.json();
}

export async function removeConversationMessageReaction(
  conversationId: string,
  messageId: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}/reaction`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('remove message reaction failed');
  return res.json();
}

export async function editConversationMessage(
  conversationId: string,
  messageId: string,
  text: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'edit conversation message failed');
  }
  return res.json();
}

export async function deleteConversationMessage(
  conversationId: string,
  messageId: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'delete conversation message failed');
  }
  return res.json();
}

export async function fetchPinnedMessages(conversationId: string): Promise<ConversationPinnedMessageDto[]> {
  const res = await apiFetch(`/api/conversations/${conversationId}/pinned`);
  if (!res.ok) throw new Error('fetch pinned messages failed');
  return res.json();
}

/**
 * Substring search over decrypted text captions. Server-side only for non-E2EE messages —
 * E2EE captions are never decrypted server-side, so they never appear in results.
 */
export async function searchConversationMessages(
  conversationId: string,
  query: string,
  take = 50,
): Promise<ConversationMessageDto[]> {
  const params = new URLSearchParams({ q: query, take: String(take) });
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/search?${params}`);
  if (!res.ok) throw new Error('search conversation messages failed');
  return res.json();
}

export async function pinConversationMessage(
  conversationId: string,
  messageId: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}/pin`, {
    method: 'PUT',
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'pin conversation message failed');
  }
  return res.json();
}

export async function unpinConversationMessage(
  conversationId: string,
  messageId: string,
): Promise<ConversationMessageDto> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages/${messageId}/pin`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'unpin conversation message failed');
  }
  return res.json();
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/read`, { method: 'POST' });
  if (!res.ok) throw new Error('mark conversation read failed');
}

/** Best-effort, ephemeral ping — failures are not actionable and must not surface to the user. */
export async function notifyConversationTyping(conversationId: string): Promise<void> {
  try {
    await apiFetch(`/api/conversations/${conversationId}/typing`, { method: 'POST' });
  } catch {
    // ignore — typing indicators are not critical
  }
}

export async function sendConversationDocument(
  conversationId: string,
  formData: FormData,
  options?: { onProgress?: (percent: number) => void; signal?: AbortSignal },
): Promise<ConversationMessageDto> {
  const res = await apiUpload(`/api/conversations/${conversationId}/send-document`, formData, {
    onProgress: options?.onProgress,
    signal: options?.signal,
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'send conversation document failed');
  }
  return res.json();
}

/**
 * Client-generated id for a single logical send attempt. Reusing the same id across
 * automatic retries (withNetworkRetry) and offline-queue replays lets the server dedupe
 * an at-least-once resend into the original message instead of creating a duplicate.
 */
export function createClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function sendConversationText(
  conversationId: string,
  text: string,
  replyToMessageId?: string | null,
  isE2ee = false,
  clientMessageId?: string,
): Promise<ConversationMessageDto> {
  return withNetworkRetry(async () => {
    const res = await apiFetch(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text,
        replyToMessageId: replyToMessageId ?? null,
        isE2ee,
        clientMessageId: clientMessageId ?? null,
      }),
    });
    if (!res.ok) {
      const message = await getApiErrorMessage(res);
      throw new Error(message ?? 'send conversation text failed');
    }
    return res.json();
  });
}

export async function fetchConversationMedia(
  conversationId: string,
  options?: {
    type?: 'all' | 'image' | 'document';
    q?: string;
    skip?: number;
    take?: number;
  },
): Promise<{
  items: ConversationMediaItemDto[];
  totalCount: number;
  skip: number;
  take: number;
}> {
  const params = new URLSearchParams();
  if (options?.type) params.set('type', options.type);
  if (options?.q) params.set('q', options.q);
  params.set('skip', String(options?.skip ?? 0));
  params.set('take', String(options?.take ?? 50));
  const res = await apiFetch(`/api/conversations/${conversationId}/media?${params}`);
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'fetch conversation media failed');
  }
  return res.json();
}

export async function registerConversationCryptoDevice(body: {
  deviceId: string;
  identityPublicKey: string;
  signedPreKeyPublic: string;
  signedPreKeyId: number;
  oneTimePreKeys: Array<{ preKeyId: number; publicKey: string }>;
}): Promise<{ deviceId: string; identityPublicKey: string; signedPreKeyId: number; createdAt: string }> {
  const res = await apiFetch('/api/conversations/crypto/devices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'register crypto device failed');
  }
  return res.json();
}

export async function fetchPeerPreKeyBundle(peerUserId: string): Promise<{
  userId: string;
  deviceId: string;
  identityPublicKey: string;
  signedPreKeyPublic: string;
  signedPreKeyId: number;
  oneTimePreKeyId: number | null;
  oneTimePreKeyPublic: string | null;
} | null> {
  const res = await apiFetch(`/api/conversations/crypto/users/${peerUserId}/prekeys`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'fetch peer prekeys failed');
  }
  return res.json();
}

export async function fetchConversationSendQuote(
  conversationId: string,
  pageCount: number,
  contentType: 'pdf' | 'image',
): Promise<ConversationSendQuote> {
  const res = await apiFetch(`/api/conversations/${conversationId}/send-quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageCount, contentType }),
  });
  if (!res.ok) {
    const message = await getApiErrorMessage(res);
    throw new Error(message ?? 'conversation send quote failed');
  }
  return res.json();
}
